import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { makeStyles, createStyles } from '@material-ui/core';
import {
  Grid, Button, Card, CardContent, TextField, Box, IconButton, TablePagination
} from '@mui/material';
import {
  Formik, Form, FormikProps
} from 'formik';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  addMonths, format, isAfter, isBefore, startOfDay
} from 'date-fns';
import { pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';
import _ from 'lodash';

import { useAuth } from '../../contexts/auth';
import WrapperSimple from '../../layout-components/ExampleWrapperSimple';
import Dictionary from '../../models/interfaces/dictionary';
import callService from '../../services/callService';
import NoData from '../../components/noData';
import Loading from '../../components/loading';
import subsidiaryService from '../../services/subsidiaryService';
import ExportCSV from '../../components/exportExcel';
import { CallMonthReport } from '../../models/interfaces/call';
import { PaymentTypeText } from '../../models/enums/paymentType';
import { CategoryDriverText } from '../../models/enums/categoryDriver';
import { DeviceTypeText } from '../../models/enums/deviceType';
import { toBrazilDate, toBrazilTime, toBrazilDateTime } from '../../components/mask';
import { getFirstDate, getLastDate } from '../../utils/dateHelper';
import CallsMonthPrint, { CallMonthExportRow } from './components/CallsMonthPrint';

interface Formulario {
  subsidiary: string | null;
  startDate: Date | null;
  endDate: Date | null;
}

interface OwnProps {
  subsidiaryId?: string;
}

const PDF_MAX_ROWS = 2000;

const useStyles = makeStyles(() => createStyles({
  selectRow: {
    cursor: 'default'
  }
}));

const toMoney = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const paymentLabel = (type: number) => PaymentTypeText[type as keyof typeof PaymentTypeText] || 'Indefinido';

const categoryLabel = (type: number) => CategoryDriverText[type as keyof typeof CategoryDriverText] || 'Desconhecida';

const deviceLabel = (type: number) => DeviceTypeText[type as keyof typeof DeviceTypeText] || 'Indefinido';

const page = (own: OwnProps) => {
  const [rows, setRows] = React.useState<CallMonthReport[] | undefined>();
  const current = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const [subsidiaries, setSubsidiaries] = React.useState<Dictionary[]>([]);
  const [valuesForm, setValuesForm] = React.useState<Formulario>();
  const [pageIndex, setPageIndex] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const classes = useStyles();

  let { subsidiaryId } = current;
  if (own.subsidiaryId) subsidiaryId = own.subsidiaryId;

  useEffect(() => {
    if (subsidiaryId) return;
    subsidiaryService.getByPaginationAsync(1, 200).then((result) => {
      setSubsidiaries(result.data.map((x: any) => ({
        key: x.id,
        value: x.description
      })));
    });
  }, [subsidiaryId]);

  const convertToExportData = (calls: CallMonthReport[]): CallMonthExportRow[] => calls.map((call) => ({
    Data: toBrazilDateTime(call.createdAt),
    Cliente: call.clientName,
    Condutor: call.driverName,
    Destinos: call.destinations,
    Retorno: call.hasBack ? 'Sim' : 'Não',
    Pagamento: paymentLabel(call.paymentType),
    Valor: toMoney(call.paymentValue),
    Status: call.statusHtml.description,
    Canal: call.salesChannelName || 'App/Web'
  }));

  const getRowsToExcel = (): any[] => {
    const data = (rows ?? []).map((call) => ({
      Id: call.id,
      Data: toBrazilDateTime(call.createdAt),
      Cliente: call.clientName,
      Condutor: call.driverName,
      Destinos: call.destinations,
      Retorno: call.hasBack ? 'Sim' : 'Não',
      Pagamento: paymentLabel(call.paymentType),
      Valor: call.paymentValue,
      'Valor retorno': call.paymentReturnValue,
      'Taxa franquia (%)': call.subsidiaryFeePerc,
      Status: call.statusHtml.description,
      Canal: call.salesChannelName || 'App/Web',
      Categoria: categoryLabel(call.driverCategory),
      Dispositivo: deviceLabel(call.device),
      Franquia: call.subsidiaryName,
      'Cancelada em': call.canceledAt ? toBrazilDateTime(call.canceledAt) : ''
    }));

    if (data.length > 0) {
      data.push({
        Id: '',
        Data: 'TOTAL',
        Cliente: `${data.length} chamada${data.length !== 1 ? 's' : ''}`,
        Condutor: '',
        Destinos: _.sumBy(rows, 'destinations'),
        Retorno: '',
        Pagamento: '',
        Valor: _.sumBy(rows, 'paymentValue'),
        'Valor retorno': _.sumBy(rows, 'paymentReturnValue'),
        'Taxa franquia (%)': '',
        Status: '',
        Canal: '',
        Categoria: '',
        Dispositivo: '',
        Franquia: '',
        'Cancelada em': ''
      } as any);
    }

    return data;
  };

  const exportCsv = () => {
    const data = getRowsToExcel();
    if (!data.length) {
      enqueueSnackbar('Nenhum registro para exportar', { variant: 'warning' });
      return;
    }
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(';'),
      ...data.map((row) => headers.map((header) => {
        const value = row[header] ?? '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(';'))
    ].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `chamadas_mensal_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
  };

  const generatePDF = async () => {
    try {
      const allRows = convertToExportData(rows ?? []);
      const truncated = allRows.length > PDF_MAX_ROWS;
      const exportData = truncated ? allRows.slice(0, PDF_MAX_ROWS) : allRows;
      if (truncated) {
        enqueueSnackbar('O PDF foi limitado aos primeiros 2.000 registros. Use Excel ou CSV para a lista completa.', { variant: 'info' });
      }
      const pdfDocument = (
        <CallsMonthPrint
          data={exportData}
          fileName={format(new Date(), 'yyyyMMdd_HHmmss')}
          filters={{
            subsidiary: valuesForm?.subsidiary
              ? subsidiaries.find((item) => item.key === valuesForm.subsidiary)?.value
                || current.subsidiaries?.find((item) => item.key === valuesForm.subsidiary)?.value
                || 'Selecionada'
              : current.subsidiaries?.find((item) => item.key === subsidiaryId)?.value || 'Franquia do gestor',
            period: valuesForm?.startDate && valuesForm?.endDate
              ? `${toBrazilDate(valuesForm.startDate)} a ${toBrazilDate(valuesForm.endDate)}`
              : 'Período indefinido'
          }}
          totalRecords={allRows.length}
          truncated={truncated}
        />
      );

      const blob = await pdf(pdfDocument).toBlob();
      saveAs(blob, `chamadas_mensal_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
    } catch {
      enqueueSnackbar('Erro ao gerar PDF', { variant: 'error' });
    }
  };

  const totals = React.useMemo(() => {
    if (!rows?.length) {
      return {
        calls: 0, destinations: 0, value: 0, canceled: 0
      };
    }
    return {
      calls: rows.length,
      destinations: _.sumBy(rows, 'destinations'),
      value: _.sumBy(rows, 'paymentValue'),
      canceled: rows.filter((item) => item.status === 'canceled').length
    };
  }, [rows]);

  const pagedRows = React.useMemo(() => {
    if (!rows) return [];
    const start = pageIndex * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, pageIndex, rowsPerPage]);

  return (
    <>
      <WrapperSimple sectionHeading="Relatório detalhado de chamadas por mês">
        <Formik
          initialValues={{
            subsidiary: subsidiaryId ?? null,
            startDate: getFirstDate(),
            endDate: getLastDate()
          }}
          validationSchema={Yup.object().shape({
            subsidiary: subsidiaryId ? Yup.string().notRequired().nullable() : Yup.string().required('Informe a franquia').nullable(),
            startDate: Yup.date().required('Informe a data inicial').nullable(),
            endDate: Yup.date()
              .required('Informe a data final')
              .nullable()
              .test('end-after-start', 'A data final deve ser igual ou posterior à inicial', function testEndAfterStart(endDate) {
                const { startDate } = this.parent;
                if (!startDate || !endDate) return true;
                return !isBefore(startOfDay(endDate), startOfDay(startDate));
              })
              .test('max-one-month', 'O intervalo não pode ultrapassar 1 mês', function testMaxOneMonth(endDate) {
                const { startDate } = this.parent;
                if (!startDate || !endDate) return true;
                return !isAfter(startOfDay(endDate), addMonths(startOfDay(startDate), 1));
              })
          })}
          onSubmit={async (values: Formulario, actions) => {
            setIsLoading(true);
            setValuesForm(values);
            setPageIndex(0);

            let subsidiaryIdSelected = subsidiaryId;
            if (!subsidiaryIdSelected) {
              subsidiaryIdSelected = values.subsidiary;
            }

            if (!subsidiaryIdSelected) {
              enqueueSnackbar('Informe a franquia', { variant: 'warning' });
              actions.setSubmitting(false);
              setIsLoading(false);
              return;
            }

            if (!values.startDate || !values.endDate) {
              enqueueSnackbar('Informe o período', { variant: 'warning' });
              actions.setSubmitting(false);
              setIsLoading(false);
              return;
            }

            const subsidiaryName = subsidiaries.find((item) => item.key === subsidiaryIdSelected)?.value
              || current.subsidiaries?.find((item) => item.key === subsidiaryIdSelected)?.value
              || null;

            try {
              const timezone = await subsidiaryService.getTimezone(subsidiaryIdSelected);
              const report = await callService.getDetailedByMonth(
                subsidiaryIdSelected,
                subsidiaryName,
                values.startDate,
                values.endDate,
                timezone
              );
              setRows(report);
            } catch (e: any) {
              enqueueSnackbar(e.message ?? 'Erro ao buscar chamadas no Supabase', { variant: 'error' });
              setRows([]);
            } finally {
              actions.setSubmitting(false);
              setIsLoading(false);
            }
          }}
        >
          {(props: FormikProps<Formulario>) => {
            const {
              values,
              errors,
              touched,
              handleBlur,
              isSubmitting,
              setFieldValue
            } = props;

            return (
              <Form noValidate autoComplete="off">
                <Grid container spacing={3}>
                  {subsidiaryId === undefined && (
                    <Grid item xs={12} sm={12} md={12}>
                      <Autocomplete
                        options={subsidiaries}
                        getOptionLabel={(option: Dictionary) => option.value}
                        onChange={(_e, value) => setFieldValue('subsidiary', value?.key ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            required
                            variant="outlined"
                            label="Franquia"
                            autoFocus
                            onBlur={handleBlur}
                            error={Boolean(errors.subsidiary && touched.subsidiary)}
                            helperText={errors.subsidiary && touched.subsidiary && errors.subsidiary}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4}>
                    <KeyboardDatePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Início"
                      format="dd/MM/yyyy"
                      InputLabelProps={{ shrink: true }}
                      name="startDate"
                      maxDate={values.endDate || undefined}
                      minDate={values.endDate ? addMonths(values.endDate, -1) : undefined}
                      onChange={(date: Date | null) => {
                        setFieldValue('startDate', date);
                        if (date && values.endDate) {
                          if (isBefore(startOfDay(values.endDate), startOfDay(date))) {
                            setFieldValue('endDate', date);
                          } else if (isAfter(startOfDay(values.endDate), addMonths(startOfDay(date), 1))) {
                            setFieldValue('endDate', addMonths(startOfDay(date), 1));
                          }
                        }
                      }}
                      onBlur={handleBlur}
                      value={values.startDate}
                      error={Boolean(errors.startDate && touched.startDate)}
                      helperText={errors.startDate && touched.startDate && errors.startDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <KeyboardDatePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Fim"
                      format="dd/MM/yyyy"
                      InputLabelProps={{ shrink: true }}
                      name="endDate"
                      minDate={values.startDate || undefined}
                      maxDate={values.startDate ? addMonths(values.startDate, 1) : undefined}
                      onChange={(date: Date | null) => { setFieldValue('endDate', date); }}
                      onBlur={handleBlur}
                      value={values.endDate}
                      error={Boolean(errors.endDate && touched.endDate)}
                      helperText={(errors.endDate && touched.endDate && errors.endDate) || 'Intervalo máximo de 1 mês'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} fullWidth>
                      <span className="btn-wrapper--icon">
                        <FontAwesomeIcon icon={['far', 'keyboard']} />
                      </span>
                      <span className="btn-wrapper--label">Buscar</span>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </WrapperSimple>

      {rows && rows.length > 0 && (
        <Card className="card-box mb-4">
          <div className="card-header">
            <div className="card-header--title">
              <h4>Exportar Relatório</h4>
              <span className="text-black-50">
                {totals.calls}
                {' '}
                chamada
                {totals.calls !== 1 ? 's' : ''}
                {' · '}
                {totals.destinations}
                {' destino'}
                {totals.destinations !== 1 ? 's' : ''}
                {' · '}
                {toMoney(totals.value)}
                {' · '}
                {totals.canceled}
                {' cancelada'}
                {totals.canceled !== 1 ? 's' : ''}
              </span>
            </div>
            <Box className="card-header--actions">
              <IconButton
                size="small"
                color="primary"
                className="text-primary"
                title="Exportar para PDF"
                onClick={generatePDF}
              >
                <FontAwesomeIcon icon={['far', 'file-pdf']} className="font-size-lg" />
              </IconButton>
              {' | '}
              <ExportCSV
                csvData={getRowsToExcel()}
                fileName={`chamadas_mensal_${format(new Date(), 'yyyyMMdd_HHmmss')}`}
              />
              {' | '}
              <IconButton
                size="small"
                color="primary"
                className="text-primary"
                title="Exportar para CSV"
                onClick={exportCsv}
              >
                <FontAwesomeIcon icon={['far', 'file-alt']} className="font-size-lg" />
              </IconButton>
            </Box>
          </div>
        </Card>
      )}

      {isLoading ? <Loading /> : (
        <>
          {rows && (
            rows.length > 0 ? (
              <Card className="card-box mb-4">
                <CardContent className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover text-nowrap mb-0">
                      <thead>
                        <tr>
                          <th className="bg-white text-center">Data</th>
                          <th className="bg-white text-left">Cliente</th>
                          <th className="bg-white">Condutor</th>
                          <th className="bg-white text-center">Destinos</th>
                          <th className="bg-white text-center">Pagamento</th>
                          <th className="bg-white text-right">Valor</th>
                          <th className="bg-white text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedRows.map((item) => (
                          <tr key={item.id} className={classes.selectRow}>
                            <td className="text-center">
                              <div>
                                <div>{toBrazilDate(item.createdAt)}</div>
                                <span>{toBrazilTime(item.createdAt)}</span>
                              </div>
                            </td>
                            <td>
                              <div>{item.clientName}</div>
                              <span className="text-black-50 d-block">
                                {item.salesChannelName || 'App/Web'}
                                {item.hasBack ? ' · Com retorno' : ''}
                              </span>
                            </td>
                            <td>{item.driverName}</td>
                            <td className="text-center">{item.destinations}</td>
                            <td className="text-center">{paymentLabel(item.paymentType)}</td>
                            <td className="text-right">{toMoney(item.paymentValue)}</td>
                            <td className="text-center">
                              <div className={`h-auto py-0 px-3 badge badge-${item.statusHtml.color}`}>
                                {item.statusHtml.description}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="font-weight-bold">
                        <tr>
                          <td colSpan={3} className="text-right">TOTAL</td>
                          <td className="text-center">{totals.destinations}</td>
                          <td className="text-center">-</td>
                          <td className="text-right">{toMoney(totals.value)}</td>
                          <td className="text-center">{`${totals.calls} chamadas`}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <TablePagination
                    component="div"
                    count={rows.length}
                    page={pageIndex}
                    onPageChange={(_event, newPage) => setPageIndex(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPageIndex(0);
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage="Linhas"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  />
                </CardContent>
              </Card>
            ) : <NoData message="Nenhuma chamada encontrada para o período informado." />
          )}
        </>
      )}
    </>
  );
};

export default page;
